import { action, observable } from "mobx";
import { BaasicDropdownStore, BaseEditViewStore } from 'core/stores';
import { ChangeClinicalStatusModalForm } from 'application/niko-facility-order/forms';
import { ClinicalStatusService } from 'application/clinical-status/services';
import { NikoPatientTemplateService } from "application/niko-patient/services";
import { ModalParams } from 'core/models';

// ISG-2622: clinical statuses that bill to insurance ("covered"). Mirrors the backend
// Util.IsReferenceBillableByTemplate. Two statuses are the same coverage type when both are in this set or
// both are outside it; this modal only allows same-coverage-type changes.
const COVERED_CLINICAL_STATUSES = ["Covered", "Prior Auth Obtained"];
const isCoveredClinicalStatus = (name) => COVERED_CLINICAL_STATUSES.includes(name);

class ChangeClinicalStatusModalStore extends BaseEditViewStore {
    @observable accessor patientTemplate;

    // ISG-2723 (part of ISG-2474): the candidate PSOs the rep has checked in the pop-up. Seeded from the backend's
    // all-or-nothing default (isSelectedByDefault) - every row checked when all candidates match, nothing checked when
    // at least one is Unmatched.
    @observable accessor multiDosSelectedIds = [];

    // ISG-2723: the Unmatched PSOs the rep has opened. Confirm stays locked until this covers all of them, so the rep
    // cannot credit an Unmatched order without having looked at it.
    @observable accessor multiDosViewedUnmatchedIds = [];

    // ISG-2723: true while the credit is being applied, so the pop-up's buttons cannot be pressed twice.
    @observable accessor multiDosApplying = false;

    constructor(rootStore, componentProps) {
        const nikoPatientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        const patientTemplate = componentProps.modalParams.data.patientTemplate;
        super(rootStore, {
            name: 'change-clinical-status-modal-store',
            Form: ChangeClinicalStatusModalForm,
            id: patientTemplate.id,
            authorization: 'portalSection.update',
            autoInit: false,
            actions: () => {
                return {
                    update: async (resource) => {
                        const response = await nikoPatientTemplateService.changeClinicalStatus(resource);
                        return response.data;
                    },
                };
            },
            FormClass: ChangeClinicalStatusModalForm,
        });

        this.rootStore = rootStore;
        this.modalParams = componentProps.modalParams;
        this.patientTemplate = patientTemplate;
        this.patientName = componentProps.modalParams.data.patientName;
        this.nikoPatientId = componentProps.modalParams.data.nikoPatientId;
        // ISG-2898: the portal keeps two disjoint Clinical Status lists - master patient vs non-master patient.
        // A master patient's template may only move to another master patient status.
        this.isMasterPatient = componentProps.modalParams.data.isMasterPatient === true;
        this.getSalesOrders = componentProps.modalParams.data.getSalesOrders;
        this.refreshPatientTemplates = componentProps.modalParams.data.refreshPatientTemplates;

        this.clinicalStatusService = new ClinicalStatusService(rootStore.application.baasic.apiClient);
        this.nikoPatientTemplateService = nikoPatientTemplateService;

        const currentStatusName = this.patientTemplate.clinicalStatus && this.patientTemplate.clinicalStatus.name;
        const currentIsCovered = isCoveredClinicalStatus(currentStatusName);
        this.currentIsCovered = currentIsCovered;

        this.clinicalStatusStore = new BaasicDropdownStore({
            filterable: true,
        }, {
            fetchFunc: async (term = "") => {
                const response = await this.clinicalStatusService.find({
                    rpp: 50,
                    isMasterPatient: this.isMasterPatient,
                    searchQuery: term || undefined
                });
                const items = (response && response.data && response.data.item) || [];
                // ISG-2622 / ISG-2623 / ISG-2624: never offer the current status. Every other status is selectable;
                // Submit routes to the right scenario - same coverage type (A) warns, covered -> non-covered (C) is
                // blocked, and non-covered -> covered (B) confirms then bills insurance (crediting the facility when an
                // invoice already exists).
                return items.filter(s => s.id !== this.patientTemplate.clinicalStatusId);
            }
        });

        // ISG-2622 Scenario A warning (orange, tight title/subtitle).
        this.clinicalStatusWarningModal = new ModalParams({});
        this.clinicalStatusWarningModal.yesLabel = 'MODAL.CONFIRM_LABEL';
        this.clinicalStatusWarningModal.noLabel = 'MODAL.CANCEL_LABEL';
        this.clinicalStatusWarningModal.swapButtons = true;
        this.clinicalStatusWarningModal.compactMessage = true;

        // ISG-2623 Scenario C error (red, tight title/subtitle). Blocks a covered -> non-covered change once a Niko
        // Sales Order exists; only Close + X, no Cancel, and the change is never applied.
        this.clinicalStatusErrorModal = new ModalParams({});
        this.clinicalStatusErrorModal.iconClassName = 'modal-error';
        this.clinicalStatusErrorModal.yesLabel = 'MODAL.CLOSE';
        this.clinicalStatusErrorModal.doNotShowCancel = true;
        this.clinicalStatusErrorModal.compactMessage = true;
        this.clinicalStatusErrorModal.onConfirm = () => this.clinicalStatusErrorModal.close();

        // ISG-2721 (part of ISG-2474): read-only multi-DOS credit candidate pop-up, shown (PSO page only) before the
        // non-covered -> covered credit when other creditable PSOs exist. Proceed continues the existing credit flow;
        // no fields are copied yet (that is ISG-2722 / ISG-2723).
        this.multiDosCreditCandidatesModal = new ModalParams({});

        this.fetch([
            this.initialize()
        ]);
    }

    async initialize() {
        this.form.clear();
    }

    onUpdateError(error) {
        if (this.errorActions.onUpdateError) {
            return this.errorActions.onUpdateError(error);
        }

        const data = error && error.data;
        const errors = data && Array.isArray(data.errors) ? data.errors.filter(e => e) : [];
        let message = 'An error occurred while changing the clinical status.';
        if (errors.length) {
            message = errors.join(' ');
        }
        else if (typeof data === 'string' && data) {
            message = data;
        }
        this.rootStore.notificationStore.error(message);
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;
        if (!resource || !resource.clinicalStatusId) return;

        // ISG-2623 Scenario C: covered -> non-covered is blocked once a Niko Sales Order exists. Show the red error
        // popup and do not apply the change (the second sentence is not bolded per the design).
        const newStatusName = this.clinicalStatusStore.value && this.clinicalStatusStore.value.name;
        if (this.currentIsCovered && !isCoveredClinicalStatus(newStatusName)) {
            this.clinicalStatusErrorModal.open({
                question: "Order was billed to insurance.",
                message: "To change it to non-covered (and bill the facility), please contact AR."
            });
            return;
        }

        // ISG-2624 Scenario B: non-covered -> covered. The confirmation comes first, then the multi-DOS candidate
        // lookup runs behind it (ISG-2723, Raizy 2026-09-16).
        if (!this.currentIsCovered && isCoveredClinicalStatus(newStatusName)) {
            this.confirmNonCoveredToCovered(resource, newStatusName);
            return;
        }

        // Show the Scenario A warning; keep it open (loading) until the save fully completes, then close it together
        // with the edit modal.
        this.clinicalStatusWarningModal.onConfirm = async () => {
            this.clinicalStatusWarningModal.setLoading(true);
            try {
                await this.applyChange(resource.clinicalStatusId);
            }
            finally {
                this.clinicalStatusWarningModal.setLoading(false);
                this.clinicalStatusWarningModal.close();
            }
        };
        this.clinicalStatusWarningModal.onCancel = () => {
            this.clinicalStatusWarningModal.close();
        };
        this.clinicalStatusWarningModal.open({
            question: "Clinical Status will be updated for reporting purposes.",
            message: "Please confirm to apply the change."
        });
    }

    // ISG-2624 Scenario B: non-covered -> covered, in the order Raizy laid out on ISG-2723 (2026-09-16):
    //   1. confirmation pop-up ("To proceed, please confirm that all clinical updates have been applied.");
    //   2. on Confirm, look for additional PSOs to credit, with a loader line while that runs;
    //   3. none found -> credit this PSO straight away (the backend runs ISG-2624 steps 3 and 4 and updates Niko);
    //      some found -> close the confirmation and open the multi-DOS credit pop-up; its Confirm Credit applies the
    //      credit to this PSO and every checked one with no second confirmation.
    // The wording no longer depends on whether this PSO already has an invoice, so the linked-invoice precheck that
    // only existed for that wording is gone.
    @action.bound
    confirmNonCoveredToCovered(resource, newStatusName) {
        this.clinicalStatusWarningModal.onConfirm = async () => {
            // Enter re-fires onConfirm through the key listener, so ignore it while the lookup or the credit is running.
            if (this.clinicalStatusWarningModal.loading) return;

            this.clinicalStatusWarningModal.setLoading(true);

            let candidates = [];
            let sourceInvoiceNumber = '';
            try {
                const response = await this.nikoPatientTemplateService.findMultiDosCreditCandidates(this.patientTemplate.id);
                const data = (response && response.data) || {};
                candidates = data.candidates || [];
                sourceInvoiceNumber = data.sourceInvoiceNumber || '';
            }
            catch (err) {
                // On candidate lookup failure fall through to the single-order credit; the credit itself is unaffected.
            }

            if (candidates && candidates.length) {
                this.clinicalStatusWarningModal.setLoading(false);
                this.clinicalStatusWarningModal.close();
                this.openMultiDosCreditCandidates(resource, candidates, sourceInvoiceNumber, newStatusName);
                return;
            }

            try {
                // ISG-3040: this is still a credit, so confirm it with the same message as the multi-DOS path.
                await this.applyChange(resource.clinicalStatusId, "Credit successfully applied.");
            }
            finally {
                this.clinicalStatusWarningModal.setLoading(false);
                this.clinicalStatusWarningModal.close();
            }
        };
        this.clinicalStatusWarningModal.onCancel = () => {
            this.clinicalStatusWarningModal.close();
        };
        this.clinicalStatusWarningModal.open({
            question: "To proceed, please confirm that all clinical updates have been applied.",
            loadingMessage: "Checking for additional PSOs to credit..."
        });
    }

    // ISG-2721 / ISG-2723 (part of ISG-2474): the multi-DOS credit pop-up, opened once the rep has confirmed the
    // status change and other creditable PSOs were found. Confirm Credit applies the credit immediately.
    @action.bound
    openMultiDosCreditCandidates(resource, candidates, sourceInvoiceNumber, newStatusName) {
        // ISG-2723: seed the checkboxes from the backend default and start the unmatched review from scratch every
        // time the pop-up opens.
        this.resetMultiDosSelection(candidates);
        this.multiDosCreditCandidatesModal.onConfirm = async () => {
            await this.confirmMultiDosCredit(resource);
        };
        this.multiDosCreditCandidatesModal.onCancel = () => {
            this.multiDosCreditCandidatesModal.close();
        };
        this.multiDosCreditCandidatesModal.open({
            candidates,
            // ISG-2721 (Raizy 2026-08-18): the source PSO is not listed in the table, so its invoice number goes on
            // the header line. Blank when the source PSO has no linked invoice - the header omits it.
            sourceInvoiceNumber,
            patientName: this.patientName,
            templateCategory: this.patientTemplate.subCategory && this.patientTemplate.subCategory.name,
            sourceDos: this.patientTemplate.facilityOrder && this.patientTemplate.facilityOrder.dateOfService,
            newStatus: newStatusName
        });
    }

    @action.bound
    async applyChange(clinicalStatusId, successMessage) {
        this.form.setFieldsDisabled(true);
        try {
            await this.actions.update({ id: this.patientTemplate.id, clinicalStatusId: clinicalStatusId });
            await this.refreshChangedPatientTemplates([this.patientTemplate.id]);
            await this.modalParams.close();
            setTimeout(() => successMessage
                ? this.rootStore.notificationStore.success(successMessage)
                : this.notifySuccessUpdate(this.name), 10);
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    // ISG-3039: refresh the cards of the PSOs a change touched. Pages that do not provide the per-PSO refresh keep
    // the old whole-patient refresh. The change is already saved when this runs, so a failed refresh is reported on
    // its own and never stops the caller from closing the modal and confirming the change.
    async refreshChangedPatientTemplates(patientTemplateIds) {
        try {
            if (this.refreshPatientTemplates) {
                await this.refreshPatientTemplates(patientTemplateIds);
            }
            else if (this.getSalesOrders) {
                await this.getSalesOrders();
            }
        }
        catch {
            this.rootStore.notificationStore.error('The change was saved, but the orders could not be refreshed. Reload the page to see it.');
        }
    }

    // ISG-2727 (part of ISG-2474): open the PSO page in a new browser tab, filtered to the given PSOs and with the
    // Clinical sections expanded. Insurance sections are expanded by default on that page, so only expandClinical is
    // needed. Must stay synchronous up to window.open - awaiting first loses the user gesture and popup blockers
    // would swallow the tab.
    @action.bound
    openPatientSalesOrderPage(patientTemplateIds) {
        if (!patientTemplateIds || !patientTemplateIds.length) return;

        const params = [
            `patientTemplateIds=${patientTemplateIds.join(',')}`,
            `expandClinical=true`,
        ];
        if (this.nikoPatientId) {
            params.push(`patientId=${this.nikoPatientId}`);
        }
        window.open(`${this.rootStore.routerStore
            .findRoute('master.app.main.sales-order.niko-sales-order')
            .pattern}?${params.join('&')}`, "_blank");
    }

    // ISG-2727: all candidate PSOs listed in the pop-up, plus the source PSO.
    @action.bound
    viewAllCandidates() {
        const candidates = this.multiDosCreditCandidatesModal.data.candidates || [];
        this.openPatientSalesOrderPage([this.patientTemplate.id, ...candidates.map(c => c.id)]);
        // ISG-2723: opening every PSO counts as reviewing all of the Unmatched ones.
        this.markMultiDosUnmatchedViewed(candidates.map(c => c.id));
    }

    // ISG-2727: only the Unmatched candidate PSOs listed in the pop-up, plus the source PSO.
    @action.bound
    viewUnmatchedCandidates() {
        const candidates = this.multiDosCreditCandidatesModal.data.candidates || [];
        const unmatchedIds = candidates.filter(c => !c.isMatched).map(c => c.id);
        this.openPatientSalesOrderPage([this.patientTemplate.id, ...unmatchedIds]);
        this.markMultiDosUnmatchedViewed(unmatchedIds);
    }

    // ISG-2727: a single candidate PSO - the source PSO is not included.
    @action.bound
    viewCandidate(candidate) {
        this.openPatientSalesOrderPage([candidate.id]);
        // ISG-2723: opening an Unmatched row from its own View link reviews just that one.
        this.markMultiDosUnmatchedViewed([candidate.id]);
    }

    // ISG-2723 (part of ISG-2474): the Unmatched candidate PSOs currently listed in the pop-up.
    get multiDosUnmatchedIds() {
        const candidates = (this.multiDosCreditCandidatesModal.data && this.multiDosCreditCandidatesModal.data.candidates) || [];
        return candidates.filter(c => !c.isMatched).map(c => c.id);
    }

    // ISG-2723: Confirm is locked while any Unmatched PSO is still unreviewed. With no Unmatched candidates at all it
    // is available immediately, which is the all-matched path.
    get multiDosConfirmLocked() {
        return this.multiDosUnmatchedIds.some(id => !this.multiDosViewedUnmatchedIds.includes(id));
    }

    // ISG-2723: whether a candidate row's checkbox is checked.
    isMultiDosCandidateSelected(id) {
        return this.multiDosSelectedIds.includes(id);
    }

    // ISG-2723: start a fresh selection whenever the pop-up opens - checked per the backend's all-or-nothing default,
    // with nothing reviewed yet.
    @action.bound
    resetMultiDosSelection(candidates) {
        this.multiDosSelectedIds = (candidates || []).filter(c => c.isSelectedByDefault).map(c => c.id);
        this.multiDosViewedUnmatchedIds = [];
        this.multiDosApplying = false;
    }

    // ISG-2723: the rep may check or uncheck any row, matched or not, before confirming.
    @action.bound
    toggleMultiDosCandidate(id) {
        this.multiDosSelectedIds = this.multiDosSelectedIds.includes(id)
            ? this.multiDosSelectedIds.filter(selectedId => selectedId !== id)
            : [...this.multiDosSelectedIds, id];
    }

    // ISG-2723: record that the rep has opened these PSOs; only the Unmatched ones count towards unlocking Confirm.
    @action.bound
    markMultiDosUnmatchedViewed(ids) {
        const unmatchedIds = this.multiDosUnmatchedIds;
        const newlyViewed = (ids || []).filter(id => unmatchedIds.includes(id) && !this.multiDosViewedUnmatchedIds.includes(id));
        if (newlyViewed.length) {
            this.multiDosViewedUnmatchedIds = [...this.multiDosViewedUnmatchedIds, ...newlyViewed];
        }
    }

    // ISG-2723 (part of ISG-2474): Confirm Credit. One call credits the source PSO and every checked candidate - the
    // backend copies the source's clinical fields onto each of them first. Unchecking every row is allowed and credits
    // the source PSO alone, which is the same outcome as the single-order flow.
    @action.bound
    async confirmMultiDosCredit(resource) {
        if (this.multiDosConfirmLocked || this.multiDosApplying) return;

        this.multiDosApplying = true;
        let result;
        try {
            const response = await this.nikoPatientTemplateService.applyMultiDosCredit({
                id: this.patientTemplate.id,
                clinicalStatusId: resource.clinicalStatusId,
                patientTemplateIds: [...this.multiDosSelectedIds]
            });
            result = (response && response.data) || {};
        }
        catch (err) {
            // A blocked credit (stale candidate list, non-covered status) changes nothing, so the pop-up stays open
            // and the rep can cancel or retry.
            this.multiDosApplying = false;
            return this.onUpdateError(err);
        }

        this.multiDosApplying = false;
        this.multiDosCreditCandidatesModal.close();

        // ISG-3039: refresh the source and every checked PSO, failed ones included - a failure shows up as a billing
        // validation on that PSO's card.
        const targetIds = (result.targets || []).map(t => t.id);
        await this.refreshChangedPatientTemplates([this.patientTemplate.id, ...(targetIds.length ? targetIds : this.multiDosSelectedIds)]);

        // Raizy 2026-09-16: a Niko failure is shown as a failed-import billing validation on the PSO that failed
        // (written by the backend), not here. The refreshed list above already carries those validations, so the
        // modal just closes and a success toast confirms the credit ran, whatever the per-PSO outcome - the response
        // still carries it, but the templates are where the rep reads it.
        await this.modalParams.close();
        setTimeout(() => this.rootStore.notificationStore.success("Credit successfully applied."), 10);
    }
}

export default ChangeClinicalStatusModalStore;
